/**
 * How big the phone is drawn on the stage (DESIGN §9.3).
 *
 * The frame is 360×800 dp, which is taller than the space a laptop has left
 * once the console's own chrome has taken its share — so the default is *Fit*
 * and the editor never asks the customer to scroll a page to see the bottom of
 * their own screen. The maths is here rather than in the component because it
 * is the one part of the stage that can be checked without a layout engine.
 */

import { FRAME_HEIGHT, FRAME_WIDTH } from "./PhonePreview";

/**
 * The bezel `.design-phone` draws around the screen. It is a border on a
 * `content-box` element, so it does *not* scale with the design: 8 px each
 * side at every zoom, and 16 px the fit has to give away in both directions.
 */
export const BEZEL = 8;

/** What the segmented control offers beside *Fit*. */
export const ZOOM_LEVELS = [0.5, 0.75, 1] as const;

/** Small enough for a cramped window, large enough to still read. */
export const MIN_SCALE = 0.2;

/**
 * Roughly what a 1280×720 laptop fits, used until the stage has measured
 * itself. Deliberately a shade under it: a first paint that is slightly small
 * settles outwards, one that is slightly large flashes a scrollbar.
 */
export const FALLBACK_SCALE = 0.6;

/**
 * The largest scale at which the framed phone fits `width` × `height` — the
 * stage's *content* box, so the caller's padding is already out of it.
 *
 * Rounded down to a whole percent: at the exact fit a subpixel rounding in
 * the browser's own layout is the difference between "snug" and "the right
 * edge of the bezel is clipped".
 */
export function fitScale(width: number, height: number): number {
  const across = (width - BEZEL * 2) / FRAME_WIDTH;
  const down = (height - BEZEL * 2) / FRAME_HEIGHT;
  const raw = Math.min(across, down);
  if (!Number.isFinite(raw)) return FALLBACK_SCALE;
  return Math.min(1, Math.max(MIN_SCALE, Math.floor(raw * 100) / 100));
}

/** `0.65` → `65%`. */
export function zoomLabel(scale: number): string {
  return `${Math.round(scale * 100)}%`;
}
